/**
 * Translating API failures.
 *
 * **The API does not change.** `AppError('NOT_FOUND', 'Work order not found.')`
 * keeps its English `message`, because that string is a developer and log
 * artefact — it appears in Sentry, in `audit_log`, and in the OpenAPI document,
 * and those readers are not the user. What the *client* renders is keyed on the
 * stable `code`, which is already part of the API contract and already
 * exhaustively enumerated in `packages/contracts`.
 *
 * This is the same principle as the activity stream and the preview summaries:
 * the server sends facts, the client composes the sentence. A pre-rendered
 * English message is untranslatable and would have to be re-derived.
 */

/** A resolved copy group of `{ [code]: message }`. `ResolvedCopy` leaves are
 * plain strings when they carry no placeholders, which every error message
 * does not — the record is what makes an unmapped code detectable. */
export type ErrorCopy = Readonly<Record<string, unknown>>

/**
 * Translate one error code.
 *
 * Falls back to the **server's** message rather than to a generic apology: an
 * unmapped code is usually a module-specific business rule
 * (`NC_HAS_OPEN_HOLD`, `APPROVAL_INCOMPLETE`) whose English text is precise and
 * actionable. Replacing it with "Something went wrong" to avoid showing English
 * to a French user trades a translation gap for an information loss, which is
 * the worse of the two.
 */
export function translateErrorCode(code: string | undefined, catalog: ErrorCopy, serverMessage?: string): string | undefined {
  if (code) {
    const translated = catalog[code]
    if (typeof translated === 'string') return translated
  }
  return serverMessage
}
